import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";

/** Forest-local fur response. The player still owns its cloned materials. */
export function applyScrapyardCatLighting(minna: AbstractMesh, basil: AbstractMesh): () => void {
  const scene = minna.getScene();
  const receivers = [minna, basil].flatMap(root => root.getChildMeshes().filter(mesh => mesh.skeleton));
  const previousReceivers = receivers.map(mesh => ({ mesh, receive: mesh.receiveShadows }));
  for (const mesh of receivers) mesh.receiveShadows = true;
  const coats = minna.getChildMeshes().filter(mesh => mesh.skeleton &&
    mesh.material instanceof PBRMaterial && mesh.material.albedoTexture);
  const materials = [...new Set(coats.map(mesh => mesh.material as PBRMaterial))];
  const previousSheen = materials.map(material => ({ material, enabled: material.sheen.isEnabled,
    linked: material.sheen.linkSheenWithAlbedo, intensity: material.sheen.intensity,
    roughness: material.sheen.roughness, color: material.sheen.color.clone() }));
  for (const material of materials) {
    const sheen = material.sheen;
    sheen.isEnabled = true; sheen.linkSheenWithAlbedo = false;
    sheen.intensity = 1; sheen.roughness = .65; sheen.color.set(.7, .7, .7);
  }
  const exclusions = ["minnaLight", "minnaRimLight"].flatMap(name => {
    const light = scene.getLightByName(name);
    if (!light) return [];
    const added = coats.filter(mesh => !light.excludedMeshes.includes(mesh));
    light.excludedMeshes.push(...added);
    return [{ light, added }];
  });
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    for (const { light, added } of exclusions) {
      light.excludedMeshes = light.excludedMeshes.filter(mesh => !added.includes(mesh));
    }
    for (const { mesh, receive } of previousReceivers) if (!mesh.isDisposed()) mesh.receiveShadows = receive;
    for (const prior of previousSheen) {
      if (!scene.materials.includes(prior.material)) continue;
      const sheen = prior.material.sheen;
      sheen.isEnabled = prior.enabled; sheen.linkSheenWithAlbedo = prior.linked;
      sheen.intensity = prior.intensity; sheen.roughness = prior.roughness; sheen.color.copyFrom(prior.color);
    }
  };
}
